import { IZoneSchedule, IZoneUserNumber } from "../../../../Domain/Types/FloorPlan/ZoneSchedule";
import { IZoneInventory } from "../../../../Domain/Types/FloorPlan/ZoneInventory.type";
import { IFloorPayload } from "../../../../Domain/Types/FloorPlan/FloorPayload.type";
import { useInventoryManagementActions } from "../../../../Hooks/useInventoryManagement";
import { useCategoryManagementActions } from "../../../../Hooks/useCategoryManagement";
import { ZoneInfoSidebar } from "./ZoneInfoSidebar";
import { ZoneServiceInfoSidebar } from "./ZoneServiceInfoSidebar";
import { RoomlayoutIds } from "../../../../Domain/Types/FloorPlan/RoomlayoutAssignment.type";
import { useEquipmentManagementActions } from "../../../../Hooks/useEquipmentManagement";
import { WeekdayNames } from "../../../../Domain/Types/FloorPlan/WeekdayNames.type";
import { BookingApproval } from "../../../Views/CreateFloorPlanView/Tools/useFloorplan/useFloorplan";
import { EquipmentCategoryRow } from "../EquipmentCategoryDialogContent/typings/EquipmentCategoryTableRow.type";
import { BookingPropertyRow } from "../BookingPropertyDialogContent/typings/BookingPropertyTableRow.type";
import { BookableEquipmentIds } from "../../../../Domain/Types/FloorPlan/BookableEquipmentAssignment.type";
import { CateringMenuIds } from "../../../../Domain/Types/FloorPlan/CateringMenuAssignment.type";
import { RemoteCompany } from "../../../../../../hooks/Remote/Companies/useRemoteFetchCompanies";
import { CompanyUnits } from "../../../../../../hooks/Remote/CompanyUnits/useRemoteFetchCompanyUnits";
import { Colleague } from "../../../../../../features/Connections/types/Colleague.type";

export interface ZoneInfoSidebarProps {
  height: number;
  zoneSchedules: IZoneSchedule[];
  floorPlan: IFloorPayload;
  isZnInvModalOpen: boolean;
  hasSiteId: boolean;
  setIsZnInvModalOpen: (o: boolean) => void;
  onClose: () => void;
  onClickDelete: (zoneIds: number[]) => void;
  onChangeZoneDescription: (text: string) => void;
  onChangeName: (zoneIds: number[], name: string) => void;
  onChangePerm: (
    zoneIds: number[],
    perms: {
      companies: {
        id: string;
        enforceUsage: boolean;
        activeWeekdays: WeekdayNames[];
        hoursInAdvance: number;
      }[];
      costCenterUnits: {
        id: string;
        enforceUsage: boolean;
        activeWeekdays: WeekdayNames[];
        hoursInAdvance: number;
      }[];
      users: {
        id: string;
        enforceUsage: boolean;
        activeWeekdays: WeekdayNames[];
        hoursInAdvance: number;
      }[];
    }
  ) => void;
  onChangeEnabledStatus: (ids: number[], enabled: boolean) => void;
  onChangeApproval: (ds: number[], approval: BookingApproval) => void;
  onChangeEquipCate: (ids: number[], equipCate: EquipmentCategoryRow[]) => void;
  onChangeBookProp: (ids: number[], bookProps: BookingPropertyRow[]) => void;
  onChangeUserNumber: (ids: number[], userNumber: IZoneUserNumber) => void;
  onChangeConfService: (
    id: number,
    service: {
      bookableEquipments?: BookableEquipmentIds[];
      cateringServices?: CateringMenuIds[];
      roomlayouts?: RoomlayoutIds[];
    }
  ) => void;
  updateZoneInventory: (zoneIds: number[], inventory: IZoneInventory) => void;
  inventoryManagement: useInventoryManagementActions;
  categoryManagement: useCategoryManagementActions;
  equipmentManagement: useEquipmentManagementActions;
  floorPlanName: string;
  isFloorPlanForService: boolean;
  remoteUnits: {
    remoteCompanies: RemoteCompany[] | undefined;
    remoteCompanyUnits: CompanyUnits[] | undefined;
    remoteUsers: Colleague[] | undefined;
  };
  isRemoteUnitsFetching: boolean;
  isEditable: boolean;
  setIsFieldEditing?: (isEditing: boolean) => void;
}

/** Adds location context from the floor plan shell (see `CreateFloorPlanView`). */
export type ZoneInfoSidebarSwitchProps = ZoneInfoSidebarProps & {
  locationInventoryId: number;
};

export function ZoneInfoSidebarSwitch({
  height,
  zoneSchedules,
  floorPlan,
  isZnInvModalOpen,
  hasSiteId,
  setIsZnInvModalOpen,
  onClose,
  onClickDelete,
  onChangeZoneDescription,
  onChangeName,
  onChangePerm,
  onChangeEnabledStatus,
  onChangeApproval,
  onChangeEquipCate,
  onChangeBookProp,
  onChangeUserNumber,
  onChangeConfService,
  updateZoneInventory,
  inventoryManagement,
  categoryManagement,
  equipmentManagement,
  floorPlanName,
  isFloorPlanForService,
  remoteUnits,
  isRemoteUnitsFetching,
  isEditable,
  setIsFieldEditing,
  locationInventoryId
}: Readonly<ZoneInfoSidebarSwitchProps>) {
  return (
    <>
      {isFloorPlanForService ? (
        <ZoneServiceInfoSidebar
          height={height}
          zoneSchedules={zoneSchedules}
          floorPlan={floorPlan}
          locationInventoryId={locationInventoryId}
          equipmentManagement={equipmentManagement}
          onClose={onClose}
          onChangeUserNumber={onChangeUserNumber}
          onChangeConfService={onChangeConfService}
        />
      ) : (
        <ZoneInfoSidebar
          updateZoneInventory={updateZoneInventory}
          height={height}
          zoneSchedules={zoneSchedules}
          floorPlan={floorPlan}
          inventoryManagement={inventoryManagement}
          categoryManagement={categoryManagement}
          equipmentManagement={equipmentManagement}
          isZnInvModalOpen={isZnInvModalOpen}
          hasSiteId={hasSiteId}
          floorPlanName={floorPlanName}
          setIsZnInvModalOpen={(o: boolean) => setIsZnInvModalOpen(o)}
          onClose={onClose}
          onClickDelete={onClickDelete}
          onChangeZoneDescription={onChangeZoneDescription}
          onChangeName={onChangeName}
          onChangeEnabledStatus={onChangeEnabledStatus}
          onChangeApproval={onChangeApproval}
          onChangeEquipCate={onChangeEquipCate}
          onChangeBookProp={onChangeBookProp}
          onChangePerm={onChangePerm}
          onChangeUserNumber={onChangeUserNumber}
          onChangeConfService={onChangeConfService}
          isFloorPlanForService={isFloorPlanForService}
          remoteUnits={remoteUnits}
          isRemoteUnitsFetching={isRemoteUnitsFetching}
          isEditable={isEditable}
          setIsFieldEditing={setIsFieldEditing}
        />
      )}
    </>
  );
}
